<template>
  <div :data-testid="dataTestid">
    <section class="w-full bg-semantic-inverse-surface-light px-4 py-10 md:py-20 text-semantic-inverse-on-surface">
      <div class="w-full max-w-wrapper-lg mx-auto">
        <div class="grid lg:grid-cols-[1fr,28rem] gap-y-8 lg:gap-y-6 lg:gap-x-16">
          <V2MoleculesReviews
            class="!justify-start !p-0 !min-h-fit bg-transparent order-4 lg:order-1 mx-auto lg:mx-0 lg:mt-[6.25rem] [&>div]:!px-0 [&>div]:lg:flex-wrap"
            size="base"
            star-type="component"
            star-color="default"
            :fill-color="semanticColorLookup('inverse.primary')"
            inverted
            hide-star-string
          />

          <OrganismsFormTravelInsuranceWidget
            class="order-2 lg:order-2 lg:row-span-4 text-semantic-surface-on-surface w-full max-w-[448px] mx-auto"
            data-testid="travel-widget-form"
          />

          <div class="order-1 lg:order-3">
            <h1 class="font-hero text-semantic-sizing-hero mb-2 lg:mb-4">
              {{ heroSection.heroTitle.toUpperCase() }}
            </h1>

            <p class="text-semantic-sizing-body-lg">
              {{ heroSection.heroDescription }}
            </p>
          </div>

          <ul class="order-3 lg:order-4 text-semantic-sizing-body flex flex-col gap-2 lg:gap-4">
            <li class="flex gap-3">
              <V2IconsCheckCircle
                class="flex-none"
                :fill="semanticColorLookup('inverse.primary')"
                :icon-color="semanticColorLookup('inverse.surface-light')"
              />
              <p>
                <strong>Free quotes in minutes</strong> from a range of trusted travel insurance brands.
              </p>
            </li>

            <li class="flex gap-3">
              <V2IconsCheckCircle
                class="flex-none"
                :fill="semanticColorLookup('inverse.primary')"
                :icon-color="semanticColorLookup('inverse.surface-light')"
              />
              <p>
                <strong>Exclusive discounts</strong> for Compare Club customers.
              </p>
            </li>
            <li class="flex gap-3">
              <V2IconsCheckCircle
                class="flex-none"
                :fill="semanticColorLookup('inverse.primary')"
                :icon-color="semanticColorLookup('inverse.surface-light')"
              />
              <p>
                Match your policy by varying excess levels.
              </p>
            </li>
            <li class="flex gap-3">
              <V2IconsCheckCircle
                class="flex-none"
                :fill="semanticColorLookup('inverse.primary')"
                :icon-color="semanticColorLookup('inverse.surface-light')"
              />
              <p>
                Add optional extras such as <strong>Cruise</strong>, <strong>Snow Sports</strong> and <strong>Rental Vehicle Excess</strong>.
              </p>
            </li>                        
          </ul>
        </div>
      </div>
    </section>

    <V2OrganismHomeCompanyComparison
      :assets-base-url="`/icons/travel-insurance/company-logos/`"
      :companies="PARTNER_ITEMS"
      item-class="w-auto"
      :card-width="100"
      :show-more="false"
      hide-scroll-bar
      wrapper-inline-class="!grid-cols-5"
    >
      <template #header>
        Our travel insurance partners
      </template>

      <template #sub-header>
        Explore coverage from trusted names and boutique providers. We compare a range of policies from these travel insurance providers.
      </template>
    </V2OrganismHomeCompanyComparison>

    <V2MoleculesHomeIconContent
      title="Why consider travel insurance?"
      :items="WHY_CONSIDER_TRAVEL_INSURANCE"
      :items-per-row="3"
      class="!bg-semantic-surface-container-low"
    />

    <V2MoleculesHomeIconContent
      title="Find the right travel insurance for your trip"
      :items="FIND_THE_RIGHT_TRAVEL_INSURANCE"
      :items-per-row="3"
      class="!bg-semantic-surface-container-lowest"
    >
      <template #subTitle>
        Travel insurance policies usually include some or all of the following. <br />
        Terms and conditions may vary between providers, so check the policy wording
      </template>
    </V2MoleculesHomeIconContent>

    <V2OrganismHomeCoverTiers
      title="Find the right travel insurance for your trip"
      desc="Travel insurance policies usually include some or all of the following:"
      :list="TIERS_LIST"
      :hide-header-icon="false"
      :inverted="false"
      reverse-flow
    />

    <section class="bg-semantic-surface-default py-10 md:py-20 px-4">
      <div class="max-w-wrapper-sm mx-auto flex flex-col gap-6 md:gap-10 text-sm/[150%] md:text-base/[150%]">
        <h2
          class="text-2xl/[120%] md:text-4xl/[120%] font-bold text-center text-semantic-surface-on-surface"
        >
          How much does travel insurance cost?
        </h2>

        <ul
          class="text-semantic-surface-on-surface flex flex-col gap-2 md:gap-4"
        >
          <li class="text-center">
            This varies based on: 
          </li>

          <li
            v-for="(item, index) in TRAVEL_INSURANCE_COSTS"
            :key="index"
            class="relative flex gap-3"
          >
            <V2IconsCheckCircle
              width="24"
              height="24"
              class="shrink-0"
            />
            <!-- eslint-disable vue/no-v-html -->
            <p v-html="item">
            </p>
          </li>
        </ul>

        <p class="text-center ">
          For example, a 40-year-old travelling solo to the UK for two weeks might pay around $45 for a basic policy, or over $150+ for comprehensive cover.
        </p>

        <div class="w-full">
          <p class="font-bold text-center mb-2 md:mb-4">
            Here’s the average travel insurance premium for the top five international travel destinations in 2025 with no pre-existing medical conditions (based on a two-week single traveller aged 40)*
          </p>

          <div class="overflow-auto">
            <V2MoleculesTable
              :headers="TRAVEL_COSTS_TABLE_1.head"
              :items="TRAVEL_COSTS_TABLE_1.body"
              variant="alternate"
              class="mb-2 md:mb-4 w-full"
            />
          </div>
        </div>

        <div class="w-full">
          <p class="font-bold text-center mb-2 md:mb-4">
            Here’s the average travel insurance premium for the top five domestic travel destinations in 2025 with no pre-existing medical conditions (based on a two-week single traveller aged 40)*
          </p>

          <div class="overflow-auto">
            <V2MoleculesTable
              :headers="TRAVEL_COSTS_TABLE_2.head"
              :items="TRAVEL_COSTS_TABLE_2.body"
              variant="alternate"
              class="mb-2 md:mb-4 w-full"
            />
          </div>

          <p class="text-xs/[120%] md:text-sm/[120%] text-semantic-surface-on-surface-variant text-center">
            *Estimates (international and domestic) are based on comparison of over 30 different travel insurance policies, from providers that include Cover-More, NRMA, Allianz and Travel Insurance Direct.
          </p>
        </div>

        <p class="text-center">
          Domestic policies often cover cancellations, car rental excess, and lost or stolen belongings.
        </p>
      </div>
    </section>

    <V2MoleculesReviews
      layout="reviews"
      size="base"
      class="bg-semantic-surface-container-lowest"
    >
      <template #header>
        How our experts have helped Australians like you
      </template>
    </V2MoleculesReviews>

    <section class="bg-semantic-surface-container-low py-10 md:py-20 px-4 md:px-0">
      <V2MoleculesCardExpertCard
        :image="AUTHOR_ITEM.image"
        list-title="Kate's top travel insurance tips"
        :list="AUTHOR_ITEM.tips"
      >
        <template #title>
          Meet our expert, {{ AUTHOR_ITEM.name }}
        </template>

        <template #description>
          {{ AUTHOR_ITEM.description }}
        </template>
      </V2MoleculesCardExpertCard>
    </section>

    <V2OrganismFaq
      id="faq"
      class="bg-semantic-surface-default"
      title="FAQs about travel insurance"
      :list="FAQ_ITEMS"
    >
      Here are some of the most frequently asked questions we receive about travel insurance:
    </V2OrganismFaq>

    <V2MoleculesCardDisclaimerCard class="!bg-semantic-surface-container-lowest">
      <template #title>
        Things you should know
      </template>

      <template #description>
        <p>
          Compare Club Travel Insurance is an online comparison service.
          The information on this page is provided by Compare Club Australia Pty Ltd (“Compare Club”).
          Compare Club does not compare all travel insurance providers or all products offered by travel partners.
        </p>
        <br />
        <p>
          Any advice given here is general and has been prepared without considering your current objectives, financial situation or needs.
          Therefore, before acting on this advice, you should consider the appropriateness of the advice having regard to those objectives, situation or needs.
        </p>
        <br />
        <p>
          This comparison service does not necessarily compare all features that may be relevant to you.
          You should consider the insurer’s PDS prior to making the decision to purchase their product. For more information, please read our 
          <a 
            class="text-secondary underline font-bold" 
            target="_blank" 
            href="https://asset.compareclub.com.au/car-insurance/fsg_v_1_0.pdf"
          >
            Financial Services Guide (FSG)
          </a>,
          which contains further information about how our service works and how we are paid.
        </p>
      </template>
    </V2MoleculesCardDisclaimerCard>
  </div>
</template>

<script setup>
import {
  AUTHOR_ITEM,
  PARTNER_ITEMS,
  WHY_CONSIDER_TRAVEL_INSURANCE,
  FIND_THE_RIGHT_TRAVEL_INSURANCE,
  TIERS_LIST,
  TRAVEL_INSURANCE_COSTS,
  TRAVEL_COSTS_TABLE_1,
  TRAVEL_COSTS_TABLE_2,
  FAQ_ITEMS
} from '~/constants/pages/travel-insurance'

defineOptions({
  name: 'V2TemplatesTravelInsuranceLandingPage'
})

defineProps({
  heroSection: {
    type: Object,
    default: () => ({
      heroTitle: 'COVER THAT FITS YOUR TRIP, NOT SOMEONE ELSE\'S',
      heroDescription: 'From missed flights to lost bags, compare a range of trusted brands and get the right cover at the right price.'
    })
  },

  dataTestid: {
    type: String,
    default: 'travel-landing-page'
  }
})

</script>